export type Language = 'en' | 'ar' | 'az' | 'fr' | 'it' | 'pt' | 'es';

export interface FloatingCard {
  title: string;
  subtitle: string;
}

export interface FeatureItem {
  title: string;
  description: string;
}

export interface ScreenshotTranslation {
  title: string;
  subtitle: string;
}

export interface Translations {
  nav: {
    home: string;
    features: string;
    contact: string;
    downloadApp: string;
    blog: string;
  };
  hero: {
    badge: string;
    headlinePre: string;
    headlineHighlight: string;
    headlinePost: string;
    subheadline: string;
    subtitleBefore: string;
    dietModes: string;
    subtitleAfter: string;
    getEarlyAccess: string;
    seeHowItWorks: string;
    downloadOnAppStore: string;
    statsRecipes: string;
    statsRating: string;
    statsFree: string;
    socialProof: string;
    floatingCards: FloatingCard[];
  };
  features: {
    sectionTitle: string;
    sectionSubtitle: string;
    items: FeatureItem[];
  };
  howItWorks: {
    sectionTitle: string;
    sectionSubtitle: string;
    steps: FeatureItem[];
  };
  testimonials: {
    sectionTitle: string;
    sectionSubtitle: string;
    items: { quote: string; name: string; role: string }[];
  };
  ctaBanner: {
    title: string;
    subtitle: string;
    button: string;
    note: string;
  };
  contact: {
    title: string;
    subtitle: string;
    card1Title: string;
    card1Text: string;
    card2Title: string;
    card2Text: string;
    namePlaceholder: string;
    emailPlaceholder: string;
    messagePlaceholder: string;
    sendButton: string;
    sendingButton: string;
    successMessage: string;
    errorMessage: string;
  };
  modal: {
    title: string;
    subtitle: string;
    emailPlaceholder: string;
    notifyButton: string;
    submittingButton: string;
    successMessage: string;
    emailRequired: string;
    emailInvalid: string;
  };
  footer: {
    copyright: string;
    privacyPolicy: string;
    termsOfUse: string;
  };
  screenshots: ScreenshotTranslation[];
  blog: {
    subtitle: string;
    readMore: string;
    minRead: string;
    noPosts: string;
    backToBlog: string;
    relatedPosts: string;
    by: string;
    allTags: string;
    publishedOn: string;
  };
}

export const translations: Record<Language, Translations> = {
  // ─────────────────────────────── ENGLISH ───────────────────────────────
  en: {
    nav: {
      home: 'Home',
      features: 'Features',
      contact: 'Contact',
      downloadApp: 'Download App',
      blog: 'Blog',
    },
    hero: {
      badge: 'AI-Powered Cooking Assistant',
      headlinePre: 'Cook',
      headlineHighlight: 'Smarter',
      headlinePost: 'with AI',
      subheadline: '— Your Personal Kitchen Assistant',
      subtitleBefore: 'Enter your ingredients, pick your diet mode —',
      dietModes: 'Gym, Vegan, Vegetarian, or Standard',
      subtitleAfter: '— and let Cook AI generate personalised recipes that save you money and time, every day.',
      getEarlyAccess: 'Get Early Access',
      seeHowItWorks: 'See How It Works',
      downloadOnAppStore: 'Download on the App Store',
      statsRecipes: 'Recipes',
      statsRating: 'Rating',
      statsFree: 'Free',
      socialProof: 'Loved by thousands of home cooks',
      floatingCards: [
        { title: 'Save $6', subtitle: 'cooking at home' },
        { title: 'AI Recipes', subtitle: 'from your ingredients' },
        { title: '$121 Saved', subtitle: 'this month' },
        { title: '5 Diet Modes', subtitle: 'Gym · Vegan · More' },
      ],
    },
    features: {
      sectionTitle: 'Smarter Cooking, Your Way',
      sectionSubtitle: 'From personalized diets to ingredient management, Cook AI adapts to you — not the other way around.',
      items: [
        { title: '4 Personalized Diet Modes', description: 'Choose Gym, Vegan, Vegetarian, or Standard. Every recipe adapts to your lifestyle and fitness goals automatically.' },
        { title: 'AI Recipe Generator', description: 'Get instant recipes from ingredients you have. AI creates personalized dishes that match your taste and dietary preferences.' },
        { title: 'Cooking Insights & Analytics', description: 'Track your favorite recipes, cooking frequency, and dietary habits with beautiful visual charts and progress reports.' },
        { title: 'Smart Ingredient Swaps', description: "Don't have an ingredient? Get instant AI substitutions that maintain taste and nutritional value perfectly." },
        { title: 'Step-by-Step + Timers', description: 'Follow clear instructions with built-in timers and cooking tips. Perfect results, every time you cook.' },
        { title: 'YouTube Tutorial Links', description: 'Learn with curated video tutorials for recipes and techniques. Perfect for visual learners and beginners.' },
      ],
    },
    howItWorks: {
      sectionTitle: 'How It Works',
      sectionSubtitle: 'From fridge to table in three simple steps.',
      steps: [
        { title: 'Add Your Ingredients', description: 'Type in whatever you have at home — no shopping trip needed.' },
        { title: 'Pick Your Diet Mode', description: 'Gym, Vegan, Vegetarian, or Standard — recipes adapt to your goals automatically.' },
        { title: 'Cook & Save', description: 'Follow step-by-step instructions with timers, and watch your savings grow every meal.' },
      ],
    },
    testimonials: {
      sectionTitle: 'What Home Cooks Say',
      sectionSubtitle: 'Join thousands who already cook smarter with AI.',
      items: [
        { quote: 'I stopped ordering takeout almost completely. Cook AI turns whatever is in my fridge into dinner ideas in seconds.', name: 'Sarah M.', role: 'Busy mom of two' },
        { quote: "The Gym mode is a game changer — high-protein recipes from my own groceries. I've saved over $100 this month.", name: 'Daniel K.', role: 'Fitness enthusiast' },
        { quote: 'As a student, this app pays for itself. Cheap, healthy meals from random leftover ingredients — genius.', name: 'Aylin R.', role: 'College student' },
      ],
    },
    ctaBanner: {
      title: 'Ready to Cook Smarter?',
      subtitle: "Download Cook AI for free and turn tonight's ingredients into something amazing.",
      button: 'Download on the App Store',
      note: 'Free to download · iOS',
    },
    contact: {
      title: 'Share Your Feedback',
      subtitle: "Help us build the perfect cooking app! Tell us what features you'd love to see and what cooking challenges you face.",
      card1Title: 'Send Feedback',
      card1Text: 'Share your cooking challenges and feature requests',
      card2Title: 'Get Updates',
      card2Text: "We'll keep you posted on our launch and new features",
      namePlaceholder: 'Your Name',
      emailPlaceholder: 'Your Email',
      messagePlaceholder: 'What features would you love to see? What cooking challenges do you face? Share your thoughts...',
      sendButton: 'Send Feedback',
      sendingButton: 'Sending...',
      successMessage: '✓ Thank you! Your feedback helps us build a better app!',
      errorMessage: '✗ Something went wrong. Please try again.',
    },
    modal: {
      title: 'Get Notified at Launch! 🚀',
      subtitle: "Be the first to know when Cook AI launches. We'll send you an exclusive early access invite!",
      emailPlaceholder: 'Eg. test@test.com',
      notifyButton: 'Notify Me',
      submittingButton: 'Submitting...',
      successMessage: "✓ Success! We'll notify you at launch!",
      emailRequired: 'Email is required',
      emailInvalid: 'Enter a valid email address',
    },
    footer: {
      copyright: '© 2026 Cook AI. All rights reserved. Made with ❤️ for food lovers.',
      privacyPolicy: 'Privacy Policy',
      termsOfUse: 'Terms of Use',
    },
    screenshots: [
      { title: 'Your Dishes', subtitle: 'AI finds perfect recipes from your ingredients' },
      { title: 'Recipe Detail', subtitle: 'Step-by-step with cost comparison' },
      { title: 'Cooking Insights', subtitle: 'Track activity & money saved' },
      { title: 'AI Ingredient Search', subtitle: 'Enter what you have, get a meal plan' },
    ],
    blog: {
      subtitle: 'Tips, recipes, and cooking inspiration from the Cook AI team.',
      readMore: 'Read More',
      minRead: 'min read',
      noPosts: 'No posts available in this language yet.',
      backToBlog: 'Back to Blog',
      relatedPosts: 'Related Posts',
      by: 'By',
      allTags: 'All',
      publishedOn: 'Published',
    },
  },

  // ─────────────────────────────── ARABIC ────────────────────────────────
  ar: {
    nav: {
      home: 'الرئيسية',
      features: 'المميزات',
      contact: 'تواصل معنا',
      downloadApp: 'تحميل التطبيق',
      blog: 'مدونة',
    },
    hero: {
      badge: 'مساعد طهي بالذكاء الاصطناعي',
      headlinePre: 'اطبخ',
      headlineHighlight: 'بذكاء',
      headlinePost: 'مع الذكاء الاصطناعي',
      subheadline: '— مساعدك الشخصي في المطبخ',
      subtitleBefore: 'أدخل مكوناتك، اختر نظامك الغذائي —',
      dietModes: 'رياضي، نباتي صرف، نباتي، أو عادي',
      subtitleAfter: '— ودع Cook AI يولّد لك وصفات مخصصة توفر مالك ووقتك، كل يوم.',
      getEarlyAccess: 'احصل على وصول مبكر',
      seeHowItWorks: 'شاهد كيف يعمل',
      downloadOnAppStore: 'تحميل من App Store',
      statsRecipes: 'وصفة',
      statsRating: 'تقييم',
      statsFree: 'مجاني',
      socialProof: 'محبوب من آلاف الطهاة المنزليين',
      floatingCards: [
        { title: 'وفّر 6$', subtitle: 'بالطهي في البيت' },
        { title: 'وصفات بالذكاء الاصطناعي', subtitle: 'من مكوناتك' },
        { title: '121$ موفّرة', subtitle: 'هذا الشهر' },
        { title: '5 أنظمة غذائية', subtitle: 'رياضي · نباتي · المزيد' },
      ],
    },
    features: {
      sectionTitle: 'طهي أذكى، بأسلوبك',
      sectionSubtitle: 'من الأنظمة الغذائية الشخصية إلى إدارة المكونات، يتكيّف Cook AI معك — وليس العكس.',
      items: [
        { title: '4 أنظمة غذائية مخصصة', description: 'اختر رياضي أو نباتي صرف أو نباتي أو عادي. كل وصفة تتكيف تلقائياً مع نمط حياتك وأهدافك الصحية.' },
        { title: 'مولّد الوصفات بالذكاء الاصطناعي', description: 'احصل على وصفات فورية من المكونات المتوفرة لديك. يصنع الذكاء الاصطناعي أطباقاً مخصصة تناسب ذوقك ونظامك الغذائي.' },
        { title: 'تحليلات الطهي والإحصاءات', description: 'تتبّع وصفاتك المفضلة وتكرار الطهي وعاداتك الغذائية بمخططات بصرية جميلة وتقارير تقدم.' },
        { title: 'إبدالات مكونات ذكية', description: 'لا تمتلك مكوناً معيناً؟ احصل على بدائل فورية بالذكاء الاصطناعي تحافظ على الطعم والقيمة الغذائية بشكل مثالي.' },
        { title: 'خطوة بخطوة + مؤقتات', description: 'اتبع التعليمات الواضحة مع مؤقتات مدمجة ونصائح طهي. نتائج مثالية في كل مرة تطبخ فيها.' },
        { title: 'روابط دروس يوتيوب', description: 'تعلّم مع دروس فيديو مختارة للوصفات والتقنيات. مثالي للمتعلمين بصرياً والمبتدئين.' },
      ],
    },
    howItWorks: {
      sectionTitle: 'كيف يعمل',
      sectionSubtitle: 'من الثلاجة إلى المائدة في ثلاث خطوات بسيطة.',
      steps: [
        { title: 'أضف مكوناتك', description: 'اكتب ما لديك في المنزل — لا حاجة للتسوق.' },
        { title: 'اختر نظامك الغذائي', description: 'رياضي، نباتي صرف، نباتي، أو عادي — تتكيف الوصفات مع أهدافك تلقائياً.' },
        { title: 'اطبخ ووفّر', description: 'اتبع التعليمات خطوة بخطوة مع المؤقتات، وشاهد مدخراتك تنمو مع كل وجبة.' },
      ],
    },
    testimonials: {
      sectionTitle: 'ماذا يقول الطهاة المنزليون',
      sectionSubtitle: 'انضم إلى الآلاف الذين يطبخون بذكاء بالفعل مع الذكاء الاصطناعي.',
      items: [
        { quote: 'توقفت عن طلب الطعام الجاهز تقريباً. يحوّل Cook AI ما في ثلاجتي إلى أفكار عشاء في ثوانٍ.', name: 'سارة م.', role: 'أم مشغولة لطفلين' },
        { quote: 'وضع الرياضي غيّر قواعد اللعبة — وصفات غنية بالبروتين من مشترياتي. وفّرت أكثر من 100$ هذا الشهر.', name: 'دانيال ك.', role: 'مهتم باللياقة البدنية' },
        { quote: 'كطالبة، هذا التطبيق يدفع ثمن نفسه. وجبات رخيصة وصحية من بقايا المكونات — عبقري.', name: 'آيلين ر.', role: 'طالبة جامعية' },
      ],
    },
    ctaBanner: {
      title: 'مستعد للطبخ بذكاء؟',
      subtitle: 'حمّل Cook AI مجاناً وحوّل مكونات الليلة إلى شيء مذهل.',
      button: 'تحميل من App Store',
      note: 'مجاني للتحميل · iOS',
    },
    contact: {
      title: 'شارك تقييمك',
      subtitle: 'ساعدنا في بناء تطبيق الطهي المثالي! أخبرنا بالميزات التي تودّ رؤيتها وتحديات الطهي التي تواجهها.',
      card1Title: 'أرسل تقييمك',
      card1Text: 'شارك تحديات طهيك وطلبات الميزات',
      card2Title: 'احصل على تحديثات',
      card2Text: 'سنبقيك على اطلاع بإطلاقنا والميزات الجديدة',
      namePlaceholder: 'اسمك',
      emailPlaceholder: 'بريدك الإلكتروني',
      messagePlaceholder: 'ما الميزات التي تودّ رؤيتها؟ ما تحديات الطهي التي تواجهها؟ شارك أفكارك...',
      sendButton: 'إرسال التقييم',
      sendingButton: 'جارٍ الإرسال...',
      successMessage: '✓ شكراً! تقييمك يساعدنا في بناء تطبيق أفضل!',
      errorMessage: '✗ حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    },
    modal: {
      title: 'احصل على إشعار عند الإطلاق! 🚀',
      subtitle: 'كن أول من يعلم عند إطلاق Cook AI. سنرسل لك دعوة وصول مبكر حصرية!',
      emailPlaceholder: 'مثال: test@test.com',
      notifyButton: 'أبلغني',
      submittingButton: 'جارٍ التقديم...',
      successMessage: '✓ نجح! سنبلغك عند الإطلاق!',
      emailRequired: 'البريد الإلكتروني مطلوب',
      emailInvalid: 'أدخل عنوان بريد إلكتروني صالحاً',
    },
    footer: {
      copyright: '© 2026 Cook AI. جميع الحقوق محفوظة. صُنع بـ ❤️ لعشاق الطعام.',
      privacyPolicy: 'سياسة الخصوصية',
      termsOfUse: 'شروط الاستخدام',
    },
    screenshots: [
      { title: 'أطباقك', subtitle: 'يجد الذكاء الاصطناعي وصفات مثالية من مكوناتك' },
      { title: 'تفاصيل الوصفة', subtitle: 'خطوة بخطوة مع مقارنة التكاليف' },
      { title: 'إحصاءات الطهي', subtitle: 'تتبع النشاط والمال الموفّر' },
      { title: 'البحث بالمكونات', subtitle: 'أدخل ما لديك، احصل على خطة وجبات' },
    ],
    blog: {
      subtitle: 'نصائح ووصفات وإلهام للطبخ من فريق Cook AI.',
      readMore: 'اقرأ المزيد',
      minRead: 'دقيقة للقراءة',
      noPosts: 'لا توجد منشورات متاحة بهذه اللغة بعد.',
      backToBlog: 'العودة إلى المدونة',
      relatedPosts: 'منشورات ذات صلة',
      by: 'بقلم',
      allTags: 'الكل',
      publishedOn: 'نُشر في',
    },
  },

  // ──────────────────────────── AZERBAIJANI ──────────────────────────────
  az: {
    nav: {
      home: 'Ana Səhifə',
      features: 'Xüsusiyyətlər',
      contact: 'Əlaqə',
      downloadApp: 'Tətbiqi Yüklə',
      blog: 'Blog',
    },
    hero: {
      badge: 'Süni İntellekt Dəstəkli Aşpazlıq Köməkçisi',
      headlinePre: 'Daha',
      headlineHighlight: 'Ağıllı',
      headlinePost: 'Bişir, AI ilə',
      subheadline: '— Şəxsi Mətbəx Köməkçiniz',
      subtitleBefore: 'Maddələrinizi daxil edin, pəhriz rejimini seçin —',
      dietModes: 'İdman, Vegan, Vegeterian və ya Standart',
      subtitleAfter: '— Cook AI sizin üçün hər gün pul və vaxt qənaət edən fərdi reseptlər yaradacaq.',
      getEarlyAccess: 'Erkən Giriş Əldə Et',
      seeHowItWorks: 'Necə İşlədiyini Gör',
      downloadOnAppStore: 'App Store-dan Yüklə',
      statsRecipes: 'Resept',
      statsRating: 'Reytinq',
      statsFree: 'Pulsuz',
      socialProof: 'Minlərlə ev aşpazı tərəfindən sevilir',
      floatingCards: [
        { title: '$6 Qənaət Et', subtitle: 'evdə bişirməklə' },
        { title: 'AI Reseptlər', subtitle: 'maddələrinizdən' },
        { title: '$121 Qənaət Edildi', subtitle: 'bu ay' },
        { title: '5 Pəhriz Rejimi', subtitle: 'İdman · Vegan · Daha çox' },
      ],
    },
    features: {
      sectionTitle: 'Daha Ağıllı Bişirmə, Öz Üslubunuzla',
      sectionSubtitle: 'Fərdi pəhrizlərdən maddə idarəçiliyinə qədər, Cook AI sizə uyğunlaşır — əksinə deyil.',
      items: [
        { title: '4 Fərdi Pəhriz Rejimi', description: 'İdman, Vegan, Vegeterian və ya Standart seçin. Hər resept həyat tərzinizə və fitness məqsədlərinizə avtomatik uyğunlaşır.' },
        { title: 'AI Resept Generatoru', description: 'Əlinizdəki maddələrdən ani reseptlər əldə edin. AI zövqünüzə və pəhriz seçimlərinizə uyğun fərdi yeməklər yaradır.' },
        { title: 'Bişirmə Analitikası', description: 'Sevimli reseptlərinizi, bişirmə tezliyinizi və pəhriz vərdişlərinizi gözəl vizual qrafiklər və hesabatlarla izləyin.' },
        { title: 'Ağıllı Maddə Əvəzliyi', description: 'Hər hansı bir maddəniz yoxdur? Dadı və qida dəyərini mükəmməl şəkildə qoruyan ani AI əvəzlikləri əldə edin.' },
        { title: 'Addım-addım + Taymerlər', description: 'Daxili taymerlər və bişirmə məsləhətləri ilə aydın göstərişlərə əməl edin. Hər dəfə bişirdiyinizdə mükəmməl nəticə.' },
        { title: 'YouTube Dərs Bağlantıları', description: 'Reseptlər və texnikalar üçün seçilmiş video dərslər ilə öyrənin. Vizual öyrənənlər və yeni başlayanlar üçün mükəmməldir.' },
      ],
    },
    howItWorks: {
      sectionTitle: 'Necə İşləyir',
      sectionSubtitle: 'Soyuducudan süfrəyə üç sadə addımda.',
      steps: [
        { title: 'Maddələrinizi Əlavə Edin', description: 'Evdə nə varsa yazın — alış-verişə ehtiyac yoxdur.' },
        { title: 'Pəhriz Rejiminizi Seçin', description: 'İdman, Vegan, Vegeterian və ya Standart — reseptlər məqsədlərinizə avtomatik uyğunlaşır.' },
        { title: 'Bişirin və Qənaət Edin', description: 'Taymerlərlə addım-addım göstərişlərə əməl edin və hər yeməklə qənaətinizin artmasını izləyin.' },
      ],
    },
    testimonials: {
      sectionTitle: 'Ev Aşpazları Nə Deyir',
      sectionSubtitle: 'AI ilə artıq daha ağıllı bişirən minlərlə insana qoşulun.',
      items: [
        { quote: 'Hazır yemək sifarişini demək olar ki, tamamilə dayandırdım. Cook AI soyuducumdakıları saniyələr içində şam yeməyi ideyalarına çevirir.', name: 'Sarah M.', role: 'İki uşaq anası' },
        { quote: 'İdman rejimi oyunun qaydalarını dəyişdi — öz ərzaqlarımdan yüksək proteinli reseptlər. Bu ay 100$-dan çox qənaət etmişəm.', name: 'Daniel K.', role: 'Fitness həvəskarı' },
        { quote: 'Tələbə kimi bu tətbiq öz xərcini çıxarır. Qalıq maddələrdən ucuz, sağlam yeməklər — dahiyanə.', name: 'Aylin R.', role: 'Universitet tələbəsi' },
      ],
    },
    ctaBanner: {
      title: 'Daha Ağıllı Bişirməyə Hazırsınız?',
      subtitle: 'Cook AI-ni pulsuz yükləyin və bu axşamın maddələrini möhtəşəm bir şeyə çevirin.',
      button: 'App Store-dan Yüklə',
      note: 'Yükləmək pulsuzdur · iOS',
    },
    contact: {
      title: 'Rəyinizi Paylaşın',
      subtitle: 'Mükəmməl bişirmə tətbiqini yaratmağımıza kömək edin! Hansı xüsusiyyətlər görmək istədiyinizi və üzləşdiyiniz bişirmə çətinliklərini bizə deyin.',
      card1Title: 'Rəy Göndərin',
      card1Text: 'Bişirmə çətinliklərinizi və xüsusiyyət istəklərinizi paylaşın',
      card2Title: 'Yenilikləri Əldə Edin',
      card2Text: 'Buraxılışımız və yeni xüsusiyyətlər haqqında sizi məlumatlandıracağıq',
      namePlaceholder: 'Adınız',
      emailPlaceholder: 'E-poçtunuz',
      messagePlaceholder: 'Hansı xüsusiyyətlər görmək istərdiniz? Hansı bişirmə çətinlikləriniz var? Fikirlərinizi paylaşın...',
      sendButton: 'Rəy Göndər',
      sendingButton: 'Göndərilir...',
      successMessage: '✓ Təşəkkürlər! Rəyiniz daha yaxşı tətbiq yaratmağımıza kömək edir!',
      errorMessage: '✗ Nəsə səhv getdi. Yenidən cəhd edin.',
    },
    modal: {
      title: 'Buraxılışda Xəbərdar Olun! 🚀',
      subtitle: 'Cook AI-nin nə zaman buraxılacağını ilk öyrənən olun. Sizə eksklüziv erkən giriş dəvəti göndərəcəyik!',
      emailPlaceholder: 'Məs. test@test.com',
      notifyButton: 'Mənə Xəbər Ver',
      submittingButton: 'Göndərilir...',
      successMessage: '✓ Uğurlu! Sizi buraxılışda xəbərdar edəcəyik!',
      emailRequired: 'E-poçt tələb olunur',
      emailInvalid: 'Etibarlı e-poçt ünvanı daxil edin',
    },
    footer: {
      copyright: '© 2026 Cook AI. Bütün hüquqlar qorunur. Yemək sevərləri üçün ❤️ ilə hazırlanmışdır.',
      privacyPolicy: 'Məxfilik Siyasəti',
      termsOfUse: 'İstifadə Şərtləri',
    },
    screenshots: [
      { title: 'Yeməkləriniz', subtitle: 'AI maddələrinizdən mükəmməl reseptlər tapır' },
      { title: 'Resept Detalları', subtitle: 'Xərc müqayisəsi ilə addım-addım' },
      { title: 'Bişirmə Analitikası', subtitle: 'Fəaliyyəti və qənaət edilən pulu izləyin' },
      { title: 'AI Maddə Axtarışı', subtitle: 'Nə olduğunu daxil edin, yemək planı əldə edin' },
    ],
    blog: {
      subtitle: 'Cook AI komandasından məsləhətlər, reseptlər və ilham.',
      readMore: 'Daha çox oxu',
      minRead: 'dəq. oxuma',
      noPosts: 'Bu dildə hələ heç bir yazı yoxdur.',
      backToBlog: 'Bloqa qayıt',
      relatedPosts: 'Əlaqəli yazılar',
      by: 'Müəllif:',
      allTags: 'Hamısı',
      publishedOn: 'Dərc edildi',
    },
  },

  // ──────────────────────────────── FRENCH ───────────────────────────────
  fr: {
    nav: {
      home: 'Accueil',
      features: 'Fonctionnalités',
      contact: 'Contact',
      downloadApp: "Télécharger l'App",
      blog: 'Blog',
    },
    hero: {
      badge: "Assistant Culinaire Propulsé par l'IA",
      headlinePre: 'Cuisinez',
      headlineHighlight: 'Plus Intelligemment',
      headlinePost: "avec l'IA",
      subheadline: '— Votre Assistant Cuisine Personnel',
      subtitleBefore: 'Entrez vos ingrédients, choisissez votre régime —',
      dietModes: 'Gym, Végétalien, Végétarien ou Standard',
      subtitleAfter: '— et laissez Cook AI générer des recettes personnalisées qui vous font gagner du temps et de l\'argent, chaque jour.',
      getEarlyAccess: 'Accès Anticipé',
      seeHowItWorks: 'Voir Comment Ça Marche',
      downloadOnAppStore: 'Télécharger sur l\'App Store',
      statsRecipes: 'Recettes',
      statsRating: 'Note',
      statsFree: 'Gratuit',
      socialProof: 'Adoré par des milliers de cuisiniers amateurs',
      floatingCards: [
        { title: 'Économisez 6$', subtitle: 'en cuisinant chez vous' },
        { title: 'Recettes IA', subtitle: 'de vos ingrédients' },
        { title: '121$ Économisés', subtitle: 'ce mois-ci' },
        { title: '5 Régimes', subtitle: 'Gym · Vegan · Plus' },
      ],
    },
    features: {
      sectionTitle: 'Cuisinez Plus Intelligemment, À Votre Façon',
      sectionSubtitle: "Des régimes personnalisés à la gestion des ingrédients, Cook AI s'adapte à vous — pas l'inverse.",
      items: [
        { title: '4 Modes Alimentaires Personnalisés', description: 'Choisissez Gym, Végétalien, Végétarien ou Standard. Chaque recette s\'adapte automatiquement à votre style de vie et vos objectifs.' },
        { title: 'Générateur de Recettes IA', description: "Obtenez des recettes instantanées à partir de vos ingrédients. L'IA crée des plats personnalisés correspondant à vos goûts et préférences alimentaires." },
        { title: 'Analyses & Statistiques Culinaires', description: 'Suivez vos recettes préférées, votre fréquence de cuisine et vos habitudes alimentaires avec de beaux graphiques et rapports de progrès.' },
        { title: "Substitutions d'Ingrédients Intelligentes", description: "Vous manquez d'un ingrédient ? Obtenez des substitutions IA instantanées qui préservent parfaitement le goût et la valeur nutritive." },
        { title: 'Étape par Étape + Minuteries', description: 'Suivez des instructions claires avec des minuteries intégrées et des conseils de cuisine. Des résultats parfaits à chaque fois.' },
        { title: 'Liens Tutoriels YouTube', description: 'Apprenez avec des tutoriels vidéo sélectionnés pour les recettes et les techniques. Idéal pour les apprenants visuels et les débutants.' },
      ],
    },
    howItWorks: {
      sectionTitle: 'Comment Ça Marche',
      sectionSubtitle: 'Du frigo à la table en trois étapes simples.',
      steps: [
        { title: 'Ajoutez Vos Ingrédients', description: "Indiquez ce que vous avez à la maison — pas besoin de faire les courses." },
        { title: 'Choisissez Votre Régime', description: "Gym, Végétalien, Végétarien ou Standard — les recettes s'adaptent automatiquement à vos objectifs." },
        { title: 'Cuisinez & Économisez', description: 'Suivez les instructions étape par étape avec minuteries, et regardez vos économies grandir à chaque repas.' },
      ],
    },
    testimonials: {
      sectionTitle: 'Ce Que Disent les Cuisiniers',
      sectionSubtitle: "Rejoignez des milliers de personnes qui cuisinent déjà plus intelligemment avec l'IA.",
      items: [
        { quote: "J'ai presque complètement arrêté de commander à emporter. Cook AI transforme le contenu de mon frigo en idées de dîner en quelques secondes.", name: 'Sarah M.', role: 'Maman débordée de deux enfants' },
        { quote: "Le mode Gym change tout — des recettes riches en protéines avec mes propres courses. J'ai économisé plus de 100$ ce mois-ci.", name: 'Daniel K.', role: 'Passionné de fitness' },
        { quote: "En tant qu'étudiante, cette app se rentabilise toute seule. Des repas sains et pas chers avec des restes d'ingrédients — génial.", name: 'Aylin R.', role: 'Étudiante' },
      ],
    },
    ctaBanner: {
      title: 'Prêt à Cuisiner Plus Intelligemment ?',
      subtitle: "Téléchargez Cook AI gratuitement et transformez les ingrédients de ce soir en quelque chose d'incroyable.",
      button: "Télécharger sur l'App Store",
      note: 'Téléchargement gratuit · iOS',
    },
    contact: {
      title: 'Partagez Vos Commentaires',
      subtitle: "Aidez-nous à créer l'application de cuisine parfaite ! Dites-nous quelles fonctionnalités vous aimeriez voir et les défis culinaires que vous rencontrez.",
      card1Title: 'Envoyer des Commentaires',
      card1Text: 'Partagez vos défis culinaires et vos demandes de fonctionnalités',
      card2Title: 'Recevoir des Mises à Jour',
      card2Text: 'Nous vous tiendrons informé de notre lancement et des nouvelles fonctionnalités',
      namePlaceholder: 'Votre Nom',
      emailPlaceholder: 'Votre E-mail',
      messagePlaceholder: 'Quelles fonctionnalités aimeriez-vous voir ? Quels défis culinaires rencontrez-vous ? Partagez vos idées...',
      sendButton: 'Envoyer les Commentaires',
      sendingButton: 'Envoi en cours...',
      successMessage: "✓ Merci ! Vos commentaires nous aident à créer une meilleure application !",
      errorMessage: '✗ Quelque chose a mal tourné. Veuillez réessayer.',
    },
    modal: {
      title: 'Soyez Notifié au Lancement ! 🚀',
      subtitle: "Soyez le premier à savoir quand Cook AI se lance. Nous vous enverrons une invitation d'accès anticipé exclusive !",
      emailPlaceholder: 'Ex. test@test.com',
      notifyButton: "M'avertir",
      submittingButton: 'Envoi...',
      successMessage: '✓ Succès ! Nous vous notifierons au lancement !',
      emailRequired: "L'e-mail est requis",
      emailInvalid: 'Entrez une adresse e-mail valide',
    },
    footer: {
      copyright: "© 2026 Cook AI. Tous droits réservés. Fait avec ❤️ pour les amoureux de la cuisine.",
      privacyPolicy: 'Politique de Confidentialité',
      termsOfUse: "Conditions d'Utilisation",
    },
    screenshots: [
      { title: 'Vos Plats', subtitle: "L'IA trouve des recettes parfaites à partir de vos ingrédients" },
      { title: 'Détails de la Recette', subtitle: 'Étape par étape avec comparaison des coûts' },
      { title: 'Analyses de Cuisine', subtitle: "Suivez l'activité et l'argent économisé" },
      { title: "Recherche d'Ingrédients IA", subtitle: 'Entrez ce que vous avez, obtenez un plan de repas' },
    ],
    blog: {
      subtitle: "Conseils, recettes et inspiration culinaire de l'équipe Cook AI.",
      readMore: 'Lire la suite',
      minRead: 'min de lecture',
      noPosts: "Aucun article disponible dans cette langue pour l'instant.",
      backToBlog: 'Retour au Blog',
      relatedPosts: 'Articles similaires',
      by: 'Par',
      allTags: 'Tous',
      publishedOn: 'Publié le',
    },
  },

  // ──────────────────────────────── ITALIAN ──────────────────────────────
  it: {
    nav: {
      home: 'Home',
      features: 'Funzionalità',
      contact: 'Contatto',
      downloadApp: "Scarica l'App",
      blog: 'Blog',
    },
    hero: {
      badge: "Assistente Culinario Potenziato dall'IA",
      headlinePre: 'Cucina',
      headlineHighlight: 'Più Intelligente',
      headlinePost: "con l'IA",
      subheadline: '— Il Tuo Assistente Personale in Cucina',
      subtitleBefore: 'Inserisci i tuoi ingredienti, scegli il tuo regime alimentare —',
      dietModes: 'Palestra, Vegano, Vegetariano o Standard',
      subtitleAfter: "— e lascia che Cook AI generi ricette personalizzate che ti fanno risparmiare denaro e tempo, ogni giorno.",
      getEarlyAccess: 'Accesso Anticipato',
      seeHowItWorks: 'Scopri Come Funziona',
      downloadOnAppStore: 'Scarica su App Store',
      statsRecipes: 'Ricette',
      statsRating: 'Valutazione',
      statsFree: 'Gratis',
      socialProof: 'Amato da migliaia di cuochi casalinghi',
      floatingCards: [
        { title: 'Risparmia $6', subtitle: 'cucinando a casa' },
        { title: 'Ricette IA', subtitle: 'dai tuoi ingredienti' },
        { title: '$121 Risparmiati', subtitle: 'questo mese' },
        { title: '5 Regimi Alimentari', subtitle: 'Palestra · Vegano · Altro' },
      ],
    },
    features: {
      sectionTitle: 'Cucina Più Intelligente, a Modo Tuo',
      sectionSubtitle: 'Dalle diete personalizzate alla gestione degli ingredienti, Cook AI si adatta a te — non il contrario.',
      items: [
        { title: '4 Modalità Dietetiche Personalizzate', description: 'Scegli Palestra, Vegano, Vegetariano o Standard. Ogni ricetta si adatta automaticamente al tuo stile di vita e ai tuoi obiettivi.' },
        { title: 'Generatore di Ricette IA', description: "Ottieni ricette istantanee dagli ingredienti che hai. L'IA crea piatti personalizzati che corrispondono ai tuoi gusti e preferenze dietetiche." },
        { title: 'Analisi & Statistiche di Cucina', description: 'Traccia le tue ricette preferite, la frequenza di cottura e le abitudini alimentari con bellissimi grafici visivi e report di progresso.' },
        { title: 'Sostituti di Ingredienti Intelligenti', description: 'Non hai un ingrediente? Ottieni sostituzioni IA istantanee che mantengono perfettamente il sapore e il valore nutritivo.' },
        { title: 'Passo dopo Passo + Timer', description: 'Segui istruzioni chiare con timer integrati e consigli di cucina. Risultati perfetti, ogni volta che cucini.' },
        { title: 'Link ai Tutorial YouTube', description: 'Impara con video tutorial selezionati per ricette e tecniche. Perfetto per chi impara visivamente e per i principianti.' },
      ],
    },
    howItWorks: {
      sectionTitle: 'Come Funziona',
      sectionSubtitle: 'Dal frigo alla tavola in tre semplici passaggi.',
      steps: [
        { title: 'Aggiungi i Tuoi Ingredienti', description: 'Scrivi quello che hai in casa — nessuna spesa necessaria.' },
        { title: 'Scegli la Tua Dieta', description: 'Palestra, Vegano, Vegetariano o Standard — le ricette si adattano automaticamente ai tuoi obiettivi.' },
        { title: 'Cucina & Risparmia', description: 'Segui le istruzioni passo dopo passo con i timer e guarda i tuoi risparmi crescere a ogni pasto.' },
      ],
    },
    testimonials: {
      sectionTitle: 'Cosa Dicono i Cuochi',
      sectionSubtitle: "Unisciti alle migliaia di persone che già cucinano in modo più intelligente con l'IA.",
      items: [
        { quote: 'Ho quasi smesso di ordinare cibo da asporto. Cook AI trasforma quello che ho in frigo in idee per la cena in pochi secondi.', name: 'Sarah M.', role: 'Mamma impegnata di due bambini' },
        { quote: 'La modalità Palestra è una svolta — ricette proteiche con la mia spesa. Ho risparmiato oltre 100$ questo mese.', name: 'Daniel K.', role: 'Appassionato di fitness' },
        { quote: 'Da studentessa, questa app si ripaga da sola. Pasti economici e sani da ingredienti avanzati — geniale.', name: 'Aylin R.', role: 'Studentessa universitaria' },
      ],
    },
    ctaBanner: {
      title: 'Pronto a Cucinare Più Intelligente?',
      subtitle: 'Scarica Cook AI gratis e trasforma gli ingredienti di stasera in qualcosa di straordinario.',
      button: 'Scarica su App Store',
      note: 'Download gratuito · iOS',
    },
    contact: {
      title: 'Condividi il Tuo Feedback',
      subtitle: "Aiutaci a creare l'app di cucina perfetta! Dicci quali funzionalità vorresti vedere e le sfide culinarie che affronti.",
      card1Title: 'Invia Feedback',
      card1Text: 'Condividi le tue sfide culinarie e le richieste di funzionalità',
      card2Title: 'Ricevi Aggiornamenti',
      card2Text: 'Ti terremo aggiornato sul nostro lancio e sulle nuove funzionalità',
      namePlaceholder: 'Il Tuo Nome',
      emailPlaceholder: 'La Tua Email',
      messagePlaceholder: 'Quali funzionalità vorresti vedere? Quali sfide culinarie affronti? Condividi i tuoi pensieri...',
      sendButton: 'Invia Feedback',
      sendingButton: 'Invio in corso...',
      successMessage: "✓ Grazie! Il tuo feedback ci aiuta a creare un'app migliore!",
      errorMessage: '✗ Qualcosa è andato storto. Riprova.',
    },
    modal: {
      title: 'Ricevi una Notifica al Lancio! 🚀',
      subtitle: 'Sii il primo a sapere quando Cook AI viene lanciato. Ti invieremo un invito esclusivo per l\'accesso anticipato!',
      emailPlaceholder: 'Es. test@test.com',
      notifyButton: 'Avvisami',
      submittingButton: 'Invio...',
      successMessage: '✓ Successo! Ti avviseremo al lancio!',
      emailRequired: "L'email è obbligatoria",
      emailInvalid: 'Inserisci un indirizzo email valido',
    },
    footer: {
      copyright: "© 2026 Cook AI. Tutti i diritti riservati. Fatto con ❤️ per gli amanti del cibo.",
      privacyPolicy: 'Informativa sulla Privacy',
      termsOfUse: 'Termini di Utilizzo',
    },
    screenshots: [
      { title: 'I Tuoi Piatti', subtitle: "L'IA trova ricette perfette dai tuoi ingredienti" },
      { title: 'Dettagli Ricetta', subtitle: 'Passo dopo passo con confronto dei costi' },
      { title: 'Analisi di Cucina', subtitle: 'Traccia attività e denaro risparmiato' },
      { title: 'Ricerca Ingredienti IA', subtitle: 'Inserisci quello che hai, ottieni un piano pasti' },
    ],
    blog: {
      subtitle: 'Consigli, ricette e ispirazione culinaria dal team di Cook AI.',
      readMore: 'Leggi di più',
      minRead: 'min di lettura',
      noPosts: 'Nessun articolo disponibile in questa lingua.',
      backToBlog: 'Torna al Blog',
      relatedPosts: 'Articoli correlati',
      by: 'Di',
      allTags: 'Tutti',
      publishedOn: 'Pubblicato il',
    },
  },

  // ─────────────────────────────── PORTUGUESE ────────────────────────────
  pt: {
    nav: {
      home: 'Início',
      features: 'Recursos',
      contact: 'Contato',
      downloadApp: 'Baixar o App',
      blog: 'Blog',
    },
    hero: {
      badge: 'Assistente de Culinária com IA',
      headlinePre: 'Cozinhe',
      headlineHighlight: 'com Mais Inteligência',
      headlinePost: 'com IA',
      subheadline: '— Seu Assistente Pessoal de Cozinha',
      subtitleBefore: 'Insira seus ingredientes, escolha seu modo de dieta —',
      dietModes: 'Academia, Vegano, Vegetariano ou Padrão',
      subtitleAfter: '— e deixe o Cook AI gerar receitas personalizadas que economizam seu dinheiro e tempo, todos os dias.',
      getEarlyAccess: 'Acesso Antecipado',
      seeHowItWorks: 'Veja Como Funciona',
      downloadOnAppStore: 'Baixar na App Store',
      statsRecipes: 'Receitas',
      statsRating: 'Avaliação',
      statsFree: 'Grátis',
      socialProof: 'Amado por milhares de cozinheiros caseiros',
      floatingCards: [
        { title: 'Economize $6', subtitle: 'cozinhando em casa' },
        { title: 'Receitas com IA', subtitle: 'dos seus ingredientes' },
        { title: '$121 Economizados', subtitle: 'este mês' },
        { title: '5 Modos de Dieta', subtitle: 'Academia · Vegano · Mais' },
      ],
    },
    features: {
      sectionTitle: 'Cozinhe com Mais Inteligência, do Seu Jeito',
      sectionSubtitle: 'Das dietas personalizadas ao gerenciamento de ingredientes, o Cook AI se adapta a você — não ao contrário.',
      items: [
        { title: '4 Modos de Dieta Personalizados', description: 'Escolha Academia, Vegano, Vegetariano ou Padrão. Cada receita se adapta automaticamente ao seu estilo de vida e objetivos fitness.' },
        { title: 'Gerador de Receitas com IA', description: 'Obtenha receitas instantâneas dos ingredientes que você tem. A IA cria pratos personalizados que combinam com seu gosto e preferências alimentares.' },
        { title: 'Análises e Estatísticas Culinárias', description: 'Acompanhe suas receitas favoritas, frequência de cozimento e hábitos alimentares com belos gráficos visuais e relatórios de progresso.' },
        { title: 'Substituições Inteligentes de Ingredientes', description: 'Não tem um ingrediente? Obtenha substituições instantâneas de IA que mantêm o sabor e o valor nutricional perfeitamente.' },
        { title: 'Passo a Passo + Temporizadores', description: 'Siga instruções claras com temporizadores integrados e dicas de culinária. Resultados perfeitos, toda vez que você cozinhar.' },
        { title: 'Links de Tutoriais do YouTube', description: 'Aprenda com tutoriais em vídeo selecionados para receitas e técnicas. Perfeito para quem aprende visualmente e para iniciantes.' },
      ],
    },
    howItWorks: {
      sectionTitle: 'Como Funciona',
      sectionSubtitle: 'Da geladeira à mesa em três passos simples.',
      steps: [
        { title: 'Adicione Seus Ingredientes', description: 'Digite o que você tem em casa — sem precisar ir ao mercado.' },
        { title: 'Escolha Seu Modo de Dieta', description: 'Academia, Vegano, Vegetariano ou Padrão — as receitas se adaptam automaticamente aos seus objetivos.' },
        { title: 'Cozinhe & Economize', description: 'Siga as instruções passo a passo com temporizadores e veja sua economia crescer a cada refeição.' },
      ],
    },
    testimonials: {
      sectionTitle: 'O Que Dizem os Cozinheiros',
      sectionSubtitle: 'Junte-se a milhares que já cozinham de forma mais inteligente com IA.',
      items: [
        { quote: 'Praticamente parei de pedir delivery. O Cook AI transforma o que está na minha geladeira em ideias de jantar em segundos.', name: 'Sarah M.', role: 'Mãe ocupada de dois filhos' },
        { quote: 'O modo Academia mudou o jogo — receitas ricas em proteína com as minhas próprias compras. Economizei mais de $100 este mês.', name: 'Daniel K.', role: 'Entusiasta de fitness' },
        { quote: 'Como estudante, este app se paga sozinho. Refeições baratas e saudáveis com ingredientes que sobraram — genial.', name: 'Aylin R.', role: 'Estudante universitária' },
      ],
    },
    ctaBanner: {
      title: 'Pronto para Cozinhar com Mais Inteligência?',
      subtitle: 'Baixe o Cook AI gratuitamente e transforme os ingredientes de hoje em algo incrível.',
      button: 'Baixar na App Store',
      note: 'Download gratuito · iOS',
    },
    contact: {
      title: 'Compartilhe Seu Feedback',
      subtitle: 'Ajude-nos a construir o aplicativo de culinária perfeito! Conte-nos quais recursos você adoraria ver e quais desafios culinários você enfrenta.',
      card1Title: 'Enviar Feedback',
      card1Text: 'Compartilhe seus desafios culinários e solicitações de recursos',
      card2Title: 'Receber Atualizações',
      card2Text: 'Manteremos você informado sobre nosso lançamento e novos recursos',
      namePlaceholder: 'Seu Nome',
      emailPlaceholder: 'Seu E-mail',
      messagePlaceholder: 'Quais recursos você adoraria ver? Quais desafios culinários você enfrenta? Compartilhe seus pensamentos...',
      sendButton: 'Enviar Feedback',
      sendingButton: 'Enviando...',
      successMessage: '✓ Obrigado! Seu feedback nos ajuda a construir um aplicativo melhor!',
      errorMessage: '✗ Algo deu errado. Por favor, tente novamente.',
    },
    modal: {
      title: 'Seja Notificado no Lançamento! 🚀',
      subtitle: 'Seja o primeiro a saber quando o Cook AI for lançado. Enviaremos um convite exclusivo de acesso antecipado!',
      emailPlaceholder: 'Ex. test@test.com',
      notifyButton: 'Me Avisar',
      submittingButton: 'Enviando...',
      successMessage: '✓ Sucesso! Vamos te notificar no lançamento!',
      emailRequired: 'O e-mail é obrigatório',
      emailInvalid: 'Insira um endereço de e-mail válido',
    },
    footer: {
      copyright: '© 2026 Cook AI. Todos os direitos reservados. Feito com ❤️ para amantes de comida.',
      privacyPolicy: 'Política de Privacidade',
      termsOfUse: 'Termos de Uso',
    },
    screenshots: [
      { title: 'Seus Pratos', subtitle: 'A IA encontra receitas perfeitas dos seus ingredientes' },
      { title: 'Detalhes da Receita', subtitle: 'Passo a passo com comparação de custos' },
      { title: 'Análises de Culinária', subtitle: 'Acompanhe atividade e dinheiro economizado' },
      { title: 'Busca de Ingredientes IA', subtitle: 'Insira o que você tem, receba um plano de refeições' },
    ],
    blog: {
      subtitle: 'Dicas, receitas e inspiração culinária da equipe Cook AI.',
      readMore: 'Leia mais',
      minRead: 'min de leitura',
      noPosts: 'Nenhum post disponível neste idioma ainda.',
      backToBlog: 'Voltar ao Blog',
      relatedPosts: 'Posts relacionados',
      by: 'Por',
      allTags: 'Todos',
      publishedOn: 'Publicado em',
    },
  },

  // ──────────────────────────────── SPANISH ──────────────────────────────
  es: {
    nav: {
      home: 'Inicio',
      features: 'Características',
      contact: 'Contacto',
      downloadApp: 'Descargar App',
      blog: 'Blog',
    },
    hero: {
      badge: 'Asistente de Cocina con IA',
      headlinePre: 'Cocina',
      headlineHighlight: 'Más Inteligente',
      headlinePost: 'con IA',
      subheadline: '— Tu Asistente Personal de Cocina',
      subtitleBefore: 'Ingresa tus ingredientes, elige tu modo de dieta —',
      dietModes: 'Gimnasio, Vegano, Vegetariano o Estándar',
      subtitleAfter: '— y deja que Cook AI genere recetas personalizadas que te ahorran dinero y tiempo, todos los días.',
      getEarlyAccess: 'Acceso Anticipado',
      seeHowItWorks: 'Ver Cómo Funciona',
      downloadOnAppStore: 'Descargar en App Store',
      statsRecipes: 'Recetas',
      statsRating: 'Calificación',
      statsFree: 'Gratis',
      socialProof: 'Amado por miles de cocineros caseros',
      floatingCards: [
        { title: 'Ahorra $6', subtitle: 'cocinando en casa' },
        { title: 'Recetas con IA', subtitle: 'de tus ingredientes' },
        { title: '$121 Ahorrados', subtitle: 'este mes' },
        { title: '5 Modos de Dieta', subtitle: 'Gimnasio · Vegano · Más' },
      ],
    },
    features: {
      sectionTitle: 'Cocina Más Inteligente, a Tu Manera',
      sectionSubtitle: 'Desde dietas personalizadas hasta la gestión de ingredientes, Cook AI se adapta a ti — no al revés.',
      items: [
        { title: '4 Modos de Dieta Personalizados', description: 'Elige Gimnasio, Vegano, Vegetariano o Estándar. Cada receta se adapta automáticamente a tu estilo de vida y objetivos fitness.' },
        { title: 'Generador de Recetas con IA', description: 'Obtén recetas instantáneas de los ingredientes que tienes. La IA crea platos personalizados que combinan con tu gusto y preferencias dietéticas.' },
        { title: 'Análisis y Estadísticas de Cocina', description: 'Rastrea tus recetas favoritas, frecuencia de cocina y hábitos alimentarios con hermosos gráficos visuales e informes de progreso.' },
        { title: 'Sustitutos Inteligentes de Ingredientes', description: '¿No tienes un ingrediente? Obtén sustituciones instantáneas de IA que mantienen el sabor y el valor nutricional perfectamente.' },
        { title: 'Paso a Paso + Temporizadores', description: 'Sigue instrucciones claras con temporizadores integrados y consejos de cocina. Resultados perfectos, cada vez que cocines.' },
        { title: 'Links a Tutoriales de YouTube', description: 'Aprende con tutoriales de video seleccionados para recetas y técnicas. Perfecto para quienes aprenden visualmente y para principiantes.' },
      ],
    },
    howItWorks: {
      sectionTitle: 'Cómo Funciona',
      sectionSubtitle: 'De la nevera a la mesa en tres simples pasos.',
      steps: [
        { title: 'Agrega Tus Ingredientes', description: 'Escribe lo que tienes en casa — sin necesidad de ir de compras.' },
        { title: 'Elige Tu Modo de Dieta', description: 'Gimnasio, Vegano, Vegetariano o Estándar — las recetas se adaptan automáticamente a tus objetivos.' },
        { title: 'Cocina y Ahorra', description: 'Sigue las instrucciones paso a paso con temporizadores y mira crecer tus ahorros en cada comida.' },
      ],
    },
    testimonials: {
      sectionTitle: 'Lo Que Dicen los Cocineros',
      sectionSubtitle: 'Únete a miles que ya cocinan más inteligente con IA.',
      items: [
        { quote: 'Dejé de pedir comida a domicilio casi por completo. Cook AI convierte lo que hay en mi nevera en ideas para la cena en segundos.', name: 'Sarah M.', role: 'Mamá ocupada de dos hijos' },
        { quote: 'El modo Gimnasio lo cambió todo — recetas altas en proteína con mi propia compra. He ahorrado más de $100 este mes.', name: 'Daniel K.', role: 'Entusiasta del fitness' },
        { quote: 'Como estudiante, esta app se paga sola. Comidas baratas y saludables con ingredientes sobrantes — genial.', name: 'Aylin R.', role: 'Estudiante universitaria' },
      ],
    },
    ctaBanner: {
      title: '¿Listo para Cocinar Más Inteligente?',
      subtitle: 'Descarga Cook AI gratis y convierte los ingredientes de esta noche en algo increíble.',
      button: 'Descargar en App Store',
      note: 'Descarga gratuita · iOS',
    },
    contact: {
      title: 'Comparte Tu Opinión',
      subtitle: '¡Ayúdanos a construir la aplicación de cocina perfecta! Cuéntanos qué características te gustaría ver y qué desafíos culinarios enfrentas.',
      card1Title: 'Enviar Opinión',
      card1Text: 'Comparte tus desafíos culinarios y solicitudes de funciones',
      card2Title: 'Recibir Actualizaciones',
      card2Text: 'Te mantendremos al tanto de nuestro lanzamiento y nuevas funciones',
      namePlaceholder: 'Tu Nombre',
      emailPlaceholder: 'Tu Correo Electrónico',
      messagePlaceholder: '¿Qué características te gustaría ver? ¿Qué desafíos culinarios enfrentas? Comparte tus pensamientos...',
      sendButton: 'Enviar Opinión',
      sendingButton: 'Enviando...',
      successMessage: '✓ ¡Gracias! ¡Tu opinión nos ayuda a construir una mejor aplicación!',
      errorMessage: '✗ Algo salió mal. Por favor, inténtalo de nuevo.',
    },
    modal: {
      title: '¡Recibe una Notificación al Lanzamiento! 🚀',
      subtitle: 'Sé el primero en saber cuándo se lanza Cook AI. ¡Te enviaremos una invitación exclusiva de acceso anticipado!',
      emailPlaceholder: 'Ej. test@test.com',
      notifyButton: 'Avisarme',
      submittingButton: 'Enviando...',
      successMessage: '✓ ¡Éxito! ¡Te notificaremos en el lanzamiento!',
      emailRequired: 'El correo electrónico es requerido',
      emailInvalid: 'Ingresa una dirección de correo electrónico válida',
    },
    footer: {
      copyright: '© 2026 Cook AI. Todos los derechos reservados. Hecho con ❤️ para los amantes de la comida.',
      privacyPolicy: 'Política de Privacidad',
      termsOfUse: 'Términos de Uso',
    },
    screenshots: [
      { title: 'Tus Platos', subtitle: 'La IA encuentra recetas perfectas de tus ingredientes' },
      { title: 'Detalle de Receta', subtitle: 'Paso a paso con comparación de costos' },
      { title: 'Análisis de Cocina', subtitle: 'Rastrea actividad y dinero ahorrado' },
      { title: 'Búsqueda de Ingredientes IA', subtitle: 'Ingresa lo que tienes, obtén un plan de comidas' },
    ],
    blog: {
      subtitle: 'Consejos, recetas e inspiración culinaria del equipo Cook AI.',
      readMore: 'Leer más',
      minRead: 'min de lectura',
      noPosts: 'No hay publicaciones disponibles en este idioma todavía.',
      backToBlog: 'Volver al Blog',
      relatedPosts: 'Publicaciones relacionadas',
      by: 'Por',
      allTags: 'Todos',
      publishedOn: 'Publicado el',
    },
  },
};
